// Notification bars: full-width, scheduled, dismissable bars that sit ABOVE the header and push it
// (and the page) down until the shopper dismisses them. Persisted as a JSON string on
// IStorefrontSettings.notificationsConfig. Mirrored by the storefront runtime
// (ac-storefront-web: Components/Helpers/notificationsConfig.ts) — keep the two in step.
//
// Style parts reuse the generic ISearchElementStyle shape and are applied with elementStyleFrom, so
// numbers are px, fontSize is a percent, and any camelCase CSS property passes through. Each part
// also accepts a `$styleRef` to a named Storefront Style, exactly like every other styled element.

import { ISearchElementStyle } from './searchPageConfig';

/**
 * What happens after a shopper closes the bar.
 *  * `none`       — no close button at all; the bar shows until it expires or is disabled.
 *  * `session`    — comes back on the shopper's next visit (sessionStorage).
 *  * `persistent` — stays gone on this browser (localStorage).
 */
export type NotificationDismissMode = 'none' | 'session' | 'persistent';

export const DISMISS_MODE_LABELS: Record<NotificationDismissMode, string> = {
    none: 'Not dismissible',
    session: 'Until they come back',
    persistent: 'For good',
};

export interface INotificationBar {
    /** Stable identity. Doubles as the per-shopper dismissal key, so it must never be reused or re-generated. */
    id: string;
    /** Admin-only label for the list. Never shown to shoppers — that's `text`. */
    name?: string;
    /** Off without deleting, so a seasonal bar can be parked and brought back. */
    disabled?: boolean;

    // ── Content ──
    /** The message. Plain text; no markup. */
    text?: string;
    /** Optional call to action rendered after the message. Needs both label and url to render. */
    linkText?: string;
    linkUrl?: string;
    newTab?: boolean;

    /** Display order when several bars are live at once; lower sorts first (nearest the top). */
    sortOrder?: number;

    // ── Schedule (ISO 8601 instants, UTC) ──
    /** Null/absent starts immediately. */
    startsAt?: string;
    /** Null/absent never expires. Exclusive, matching Promotion.EndsAt. */
    endsAt?: string;

    // ── Dismissal ──
    /** Defaults to `session` — the least surprising of the three: shoppers get the bar back next visit. */
    dismissMode?: NotificationDismissMode;
    /**
     * Bumped by the editor whenever the bar's CONTENT changes. A shopper's stored dismissal records the
     * revision they dismissed, so a bar that has since been reworded comes back rather than staying
     * invisible to everyone who ever closed the old one. Styling-only edits deliberately don't bump it.
     */
    revision?: number;

    // ── Style parts ──
    /** The full-width band itself. */
    bar?: ISearchElementStyle;
    /** The message text. */
    message?: ISearchElementStyle;
    /** The call-to-action link. */
    link?: ISearchElementStyle;
    /** The ✕ close button (absent when dismissMode is `none`). */
    closeButton?: ISearchElementStyle;
}

export interface INotificationsConfig {
    bars?: INotificationBar[];
}

/** The style parts a bar exposes, in the order the editor stacks them. */
export const NOTIFICATION_STYLE_PARTS = ['bar', 'message', 'link', 'closeButton'] as const;
export type NotificationStylePart = typeof NOTIFICATION_STYLE_PARTS[number];

export const NOTIFICATION_PART_LABELS: Record<NotificationStylePart, string> = {
    bar: 'Bar',
    message: 'Message',
    link: 'Link',
    closeButton: 'Close button',
};

export const NOTIFICATION_PART_TOOLTIPS: Record<NotificationStylePart, string> = {
    bar: 'The full-width band: background, height, padding, borders.',
    message: 'The message text: font, size, weight, color, alignment.',
    link: 'The call-to-action after the message.',
    closeButton: 'The ✕ that dismisses the bar.',
};

export function parseNotificationsConfig(raw?: string): INotificationsConfig {
    if (!raw) return {};
    try {
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
        const bars = (parsed as INotificationsConfig).bars;
        // A bar with no id has no dismissal key and can't be addressed, so drop it rather than let it
        // render as a bar nobody can ever close.
        return { ...parsed, bars: Array.isArray(bars) ? bars.filter((b) => !!b && typeof b.id === 'string' && !!b.id) : undefined };
    } catch {
        return {};
    }
}

/** The bars an editor should list: enabled and disabled alike, in display order. */
export function orderedBars(config: INotificationsConfig): INotificationBar[] {
    return [...(config.bars ?? [])].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
}

/** Parses an ISO instant, returning undefined for absent/unparseable values so a bad date reads as "unbounded". */
function instant(iso?: string): number | undefined {
    if (!iso) return undefined;
    const ms = Date.parse(iso);
    return Number.isNaN(ms) ? undefined : ms;
}

/** Is `bar` inside its scheduled window at `now`? Absent bounds are open. `endsAt` is exclusive. */
export function isScheduledNow(bar: INotificationBar, now: number): boolean {
    const from = instant(bar.startsAt);
    const to = instant(bar.endsAt);
    if (from !== undefined && now < from) return false;
    if (to !== undefined && now >= to) return false;
    return true;
}

/**
 * Where a bar sits relative to its window right now — what the admin list badges each row with, so a
 * merchant can see at a glance why a bar they configured isn't on the storefront.
 */
export type NotificationSchedule = 'disabled' | 'scheduled' | 'live' | 'expired';

export function scheduleStateOf(bar: INotificationBar, now: number): NotificationSchedule {
    if (bar.disabled) return 'disabled';
    const from = instant(bar.startsAt);
    const to = instant(bar.endsAt);
    if (from !== undefined && now < from) return 'scheduled';
    if (to !== undefined && now >= to) return 'expired';
    return 'live';
}

/** Enabled + in-window bars, in display order. The runtime then filters these by what the shopper dismissed. */
export function liveBars(config: INotificationsConfig, now: number): INotificationBar[] {
    return orderedBars(config).filter((b) => !b.disabled && isScheduledNow(b, now));
}
